import {
  postCompareOrderDuplicateAPI,
  postCompareResultDuplicateAPI,
  postCompareOrderAPI,
  postCompareResultAPI,
  postReadResultAPI
} from '@/api';
import { replaceConvertValue, replaceDateValue } from '@/compare';
import { columnType, dataLayout, flg, viewSts } from '@/constants';

export const CompareValueMixin = {
  data() {
    return {
      viewState: viewSts.INIT, // 画面状態(0:初期表示、1:読取ルール選択後、2:予定ヘッダ読取中、3:予定明細読取中、4:実績読取中)
      item: {
        // 照合予定実績データ
        compareOrderNo: 0, //  照合予定NO
        compareResultNo: 0, //  照合実績NO
        readId: null, //  読取ID
        inputId: null, //  取込ID
        dataType: null, //  データ区分
        orderCount: 0, //  予定件数
        resultCount: 0, //  実績件数
        details: [] //  明細
      }
    };
  },
  methods: {
    /**
     * スキャンデータ解析後(親コンポーネントより)
     * @param {!object} headerValue ヘッダ値
     * @param {!object[]} detailValues 明細値配列
     * @param {?object} barcode 対象バーコード
     * @param {string} dupType 重複区分(1:重複OK、2:重複NG)
     * @returns {boolean} スキャンデータ反映成否
     */
    analyzedScanData: async function (headerValue, detailValues, barcode, dupType) {
      // 事前処理
      let ret;
      ret = await this.analyzedBefore(this.viewState, headerValue, detailValues);
      if (ret === -1) {
        this.viewState = viewSts.READ_RULE; // 読取ルール選択後
        return false;
      }

      // 次のバーコードを取得する
      let nextBarcode = null;
      if (barcode && barcode.nextBarcodeId) {
        nextBarcode = this.$store.getters.barcodes.find(function (x) {
          return x.barcodeId === barcode.nextBarcodeId;
        });
      }

      switch (this.viewState) {
        case viewSts.INIT: // 初期
          ret = await this.analyzedStep0();
          break;
        case viewSts.READ_RULE: // 読取ルール選択後
          ret = await this.analyzedStep1(
            headerValue,
            detailValues,
            barcode,
            nextBarcode,
            dupType,
            this.item
          );
          break;
        case viewSts.ORDER_HEADER: // 照合予定ヘッダ読取中
          ret = await this.analyzedStep2(headerValue, barcode, nextBarcode, this.item);
          break;
        case viewSts.ORDER_DETAIL: // 照合予定明細読取中(読取実績読取中)
          ret = await this.analyzedStep3(detailValues, barcode, nextBarcode, dupType, this.item);
          break;
        case viewSts.RESULT: // 照合実績読取中
          ret = await this.analyzedStep4(detailValues, barcode, nextBarcode, dupType, this.item);
          break;
        default:
          ret = await this.analyzedStepDefault();
          break;
      }
      if (ret === viewSts.ERROR) {
        this.viewState = viewSts.READ_RULE;
        return false;
      }

      // 事後処理
      ret = await this.analyzedAfter(ret, headerValue, detailValues);
      if (ret === viewSts.ERROR) {
        this.viewState = viewSts.READ_RULE;
        return false;
      }

      this.viewState = ret;
      return true;
    },
    /**
     * スキャンデータ解析後事後処理
     * @param {number} viewState 画面状態
     * @param {!object} _headerValue ヘッダ値
     * @param {!object[]} _detailValues 明細値配列
     * @return {number} 画面状態、-1:解析エラー
     */
    analyzedAfter: async function (viewState, _headerValue, _detailValues) {
      return viewState;
    },
    /**
     * 照合予定情報重複確認
     * @param {!object} compare 照合情報
     * @param {!object} item 照合画面情報
     * @param {number} detailIndex 明細インデックス(-1:全明細チェック)
     * @returns {?object} 照合予定情報(重複無しはcompareOrderNo=0)、NULL:重複確認エラー
     */
    checkDuplicatedCompareOrder: async function (compare, item, detailIndex) {
      const detail = item.details[detailIndex];
      const order = { readId: item.readId };

      // ヘッダ値をセット
      const headerCompares = compare.details.filter(function (x) {
        return x.dataLayout === dataLayout.HEADER;
      });
      for (let cdetail of headerCompares) {
        const headerKey = `headerValueNo${cdetail.compareNo}`;
        order[headerKey] = item[headerKey];
      }

      // 明細値をセット
      const detailCompares = compare.details.filter(function (x) {
        return x.dataLayout === dataLayout.DETAIL;
      });
      if (detailIndex === -1) {
        for (let i = 0; i < item.details.length; i++) {
          order.details = [];
          for (let cdetail of detailCompares) {
            const detailKey = `detailValueNo${cdetail.compareNo}`;
            order.details[detailKey] = item.details[i][detailKey];
          }
        }
      } else {
        for (let cdetail of detailCompares) {
          const detailKey = `detailValueNo${cdetail.compareNo}`;
          order[detailKey] = detail[detailKey];
        }
      }

      // 照合予定情報重複確認API
      const ret = await postCompareOrderDuplicateAPI(order);
      return ret?.data;
    },
    /**
     * 照合実績情報重複確認
     * @param {!object} compare 照合情報
     * @param {!object} item 画面情報
     * @param {!object} detailValue 明細値KeyValue
     * @returns {?object} 照合実績情報(重複無しはcompareResultNo=0)、NULL:重複確認エラー
     */
    checkDuplicatedCompareResult: async function (compare, item, detailValue) {
      const result = { readId: item.readId };

      // 明細値をセット
      const detailCompares = compare.details.filter(
        (cdetail) => cdetail.dataLayout === dataLayout.DETAIL && cdetail.compareTarget === flg.ON
      );

      for (let cdetail of detailCompares) {
        const detailKey = `ToCompareNo${cdetail.compareNo}`;
        result[detailKey] = detailValue[cdetail.columnId];
      }

      // 照合実績情報重複確認API
      const ret = await postCompareResultDuplicateAPI(result);
      if (!ret) return null;

      return ret.data;
    },
    /**
     * 照合予定読取(ヘッダ)
     * @param {!object} barcode バーコード情報
     * @param {!object} compare 照合情報
     * @param {number} compareNo 対象照合NO
     * @param {!object} headerValue ヘッダ値KeyValue
     * @param {!object} item 照合画面情報に値をセット
     * @returns {number} 次照合NO、0:ヘッダ無し照合、-1:項目順エラー
     */
    readOrderHeaderValue: function (barcode, compare, compareNo, headerValue, item) {
      // 照合継続する照合明細を取得する
      const headerCompares = compare.details.filter(
        (detail) => detail.dataLayout === dataLayout.HEADER && detail.compareNo >= compareNo
      );

      // ヘッダ無し照合の場合は処理しない
      if (headerCompares.length === 0) return 0;

      // ヘッダ有りでヘッダ値がない場合はエラー
      if (Object.keys(headerValue).length === 0) return -1;

      // 先頭項目の場合はヘッダバーコードIDを保持する
      if (!item.headerBarcodeId) item.headerBarcodeId = barcode.barcodeId;

      let cdetail = null;
      for (cdetail of headerCompares) {
        const key = cdetail.columnId;
        if (!(key in headerValue)) return cdetail.compareNo;

        // 日付書式指定された場合は書式値を取得する
        let formatValue;
        if (cdetail.columnType === columnType.DATE)
          formatValue = replaceDateValue(headerValue[key], cdetail.dateFormat);
        else formatValue = headerValue[key];

        // 変換IDが指定されている場合は変換値を書式値とする
        formatValue = replaceConvertValue(
          formatValue,
          cdetail.convertId,
          this.$store.getters.converts
        );

        // 照合元値をセット
        const columnKey = `headerValueNo${cdetail.compareNo}`;
        item[columnKey] = headerValue[key];
        const formatKey = `headerFormatValueNo${cdetail.compareNo}`;
        this.$set(item, formatKey, formatValue);
      }

      return cdetail ? cdetail.compareNo : 0;
    },
    /**
     * 照合予定読取(明細)
     * @param {!object} barcode バーコード情報
     * @param {!object} compare 照合情報
     * @param {number} compareNo 対象照合NO
     * @param {number} detailIndex 明細インデックス
     * @param {!object} detailValue 明細値KeyValue
     * @param {!object} item 照合画面情報に値をセット
     * @returns {number} 次照合NO、-1:項目順エラー
     */
    readOrderDetailValue: function (barcode, compare, compareNo, detailIndex, detailValue, item) {
      // 明細値がない場合はエラー
      if (Object.keys(detailValue).length === 0) return -1;

      // 先頭項目の場合は明細バーコードIDを保持する
      if (!item.detailBarcodeId) item.detailBarcodeId = barcode.barcodeId;

      // 照合継続する照合明細を取得する
      const detailCompares = compare.details.filter(
        (detail) => detail.dataLayout === dataLayout.DETAIL && detail.compareNo >= compareNo
      );

      // 照合明細を繰り返す
      let cdetail = null;
      for (cdetail of detailCompares) {
        const key = cdetail.columnId;
        if (!(key in detailValue)) return cdetail.compareNo;

        // 省略不可で値がNULL or 空の場合にエラーとする
        if (cdetail.compareTarget === flg.ON && !detailValue[key]) return -1;

        // 日付書式指定された場合は書式値を取得する
        let formatValue = replaceDateValue(detailValue[key], cdetail.dateFormat);

        // 変換IDが指定されている場合は変換値を書式値とする
        formatValue = replaceConvertValue(
          formatValue,
          cdetail.convertId,
          this.$store.getters.converts
        );

        // 照合元値をセット
        while (item.details.length < detailIndex + 1) item.details.push({ compareFlg: flg.ETC });
        const columnKey = `detailValueNo${cdetail.compareNo}`;
        item.details[detailIndex][columnKey] = detailValue[key];
        const formatKey = `detailFormatValueNo${cdetail.compareNo}`;
        this.$set(item, formatKey, formatValue);
        this.$set(item.details[detailIndex], formatKey, formatValue);
      }

      return cdetail ? cdetail.compareNo : 0;
    },
    /**
     * 画面項目の明細インデックス検索
     * @param {!object} compare 照合情報
     * @param {!object} item 実績画面情報
     * @param {string[]} detailValue 明細値
     * @returns {number} 明細インデックス(1:1照合の場合0固定、見つからない場合0固定)
     */
    searchItemDetailIndex: function (compare, item, detailValue) {
      // ヘッダ無の場合は0固定
      const headerField = compare.details.find(function (x) {
        return x.dataLayout === dataLayout.HEADER;
      });
      if (!headerField) return 0;

      // ヘッダ有の場合は1項目目が一致するインデックスを返す
      const cdetail = compare.details.find(function (x) {
        return x.dataLayout === dataLayout.DETAIL && x.compareNo === 1;
      });
      if (cdetail) {
        const index = item.details.findIndex(function (x) {
          const columnKey = `detailValueNo${cdetail.compareNo}`;
          return x[columnKey] === detailValue[cdetail.columnId];
        });
        return index !== -1 ? index : 0;
      } else {
        return 0;
      }
    },
    /**
     * 明細照合処理
     * @param {!object} barcode バーコード情報
     * @param {!object} compare 照合情報
     * @param {number} compareNo 照合NO
     * @param {!object} item 実績画面情報
     * @param {number} detailIndexes 明細インデックス配列
     * @param {string[]} detailValue 明細値(照合値)
     * @returns {number} 0:照合完了、-1:項目順エラー
     */
    compareDetailValue: function (barcode, compare, compareNo, item, detailIndexes, detailValue) {
      // 先頭項目の場合は照合バーコードIDを保持する
      if (!item.barcodeId) item.barcodeId = barcode.barcodeId;

      // 照合項目を取得
      const detailCompares = compare.details.filter(function (x) {
        return x.dataLayout === dataLayout.DETAIL && x.compareNo >= compareNo;
      });

      const toValues = {};
      for (const cdetail of detailCompares) {
        // 照合値の対象フィールドが存在するかをチェックする
        let toValue = null;
        if (cdetail.columnId in detailValue) {
          toValue = detailValue[cdetail.columnId];
        } else {
          if (cdetail.fixedValue) {
            // 固定値の場合は照合先に固定値
            toValue = cdetail.fixedValue;
          } else if (cdetail.compareTarget === flg.OFF) {
            // 照合対象外の場合は照合先は値なし
            toValue = null;
          }
        }

        // 照合値を保持
        const toColumnKey = `toCompareNo${cdetail.compareNo}`;
        toValues[toColumnKey] = toValue;
      }

      const indexes = [];
      for (let i = 0; i < detailIndexes.length; i++) {
        const detail = item.details[detailIndexes[i]];
        let addIndex = true;

        for (const cdetail of detailCompares) {
          // 照合値セット
          const columnKey = `detailValueNo${cdetail.compareNo}`;
          const toColumnKey = `toCompareNo${cdetail.compareNo}`;
          const fromValue = detail[columnKey];
          const toValue = toValues[toColumnKey];

          // 照合NGはインデックス保持対象外
          if (fromValue !== toValue && cdetail.compareTarget === flg.ON) addIndex = false;
        }

        // 照合OKの場合は対象インデックスを保持する
        if (addIndex) indexes.push(i);
      }

      // 保持したインデックスが0件の場合は
      if (indexes.length === 0) {
        // 照合先なしは処理終了
        item.details.unshift({
          toCompareNo1: toValues['toCompareNo1'],
          toCompareNo2: toValues['toCompareNo2'],
          toCompareNo3: toValues['toCompareNo3'],
          toCompareNo4: toValues['toCompareNo4'],
          toCompareNo5: toValues['toCompareNo5'],
          toCompareNo6: toValues['toCompareNo6'],
          toCompareNo7: toValues['toCompareNo7'],
          toCompareNo8: toValues['toCompareNo8'],
          toCompareNo9: toValues['toCompareNo9'],
          toCompareNo10: toValues['toCompareNo10'],
          compareFlg: flg.ON,
          compareErr: `照合対象なしNG`
        });
        return -1;
      } else {
        // 照合先を反映する
        const detail = item.details[indexes[0]];
        detail['toCompareNo1'] = toValues['toCompareNo1'];
        detail['toCompareNo2'] = toValues['toCompareNo2'];
        detail['toCompareNo3'] = toValues['toCompareNo3'];
        detail['toCompareNo4'] = toValues['toCompareNo4'];
        detail['toCompareNo5'] = toValues['toCompareNo5'];
        detail['toCompareNo6'] = toValues['toCompareNo6'];
        detail['toCompareNo7'] = toValues['toCompareNo7'];
        detail['toCompareNo8'] = toValues['toCompareNo8'];
        detail['toCompareNo9'] = toValues['toCompareNo9'];
        detail['toCompareNo10'] = toValues['toCompareNo10'];
        detail['compareFlg'] = flg.OFF;
        detail['compareErr'] = null;
        // 照合完了
        detailIndexes.splice(0);
        detailIndexes.push(indexes);
        return 0;
      }
    },
    /**
     * 照合予定書き込み
     * @param {!object} compare 照合情報
     * @param {!object} item 照合実績画面情報
     * @param {number} detailIndex 明細インデックス
     * @returns {?object} 照合予定情報、null:エラー
     */
    writeCompareOrder: async function (compare, item, detailIndex) {
      const detail = item.details[detailIndex];
      const order = {
        compareOrderNo: item.compareOrderNo, // 照合予定NO
        readId: item.readId, // 読取ID
        dataType: item.dataType, // データ区分
        headerBarcodeId: item.headerBarcodeId, // ヘッダバーコードID
        detailBarcodeId: item.detailBarcodeId // 明細バーコードID
      };

      // ヘッダ値をセット
      const headerCompares = compare.details.filter(function (x) {
        return x.dataLayout === dataLayout.HEADER;
      });
      for (let cdetail of headerCompares) {
        const headerKey = `headerValueNo${cdetail.compareNo}`;
        order[headerKey] = item[headerKey];
      }

      // 明細値をセット
      const detailCompares = compare.details.filter(function (x) {
        return x.dataLayout === dataLayout.DETAIL;
      });
      for (let cdetail of detailCompares) {
        const detailKey = `detailValueNo${cdetail.compareNo}`;
        order[detailKey] = detail[detailKey];
      }

      // 照合予定登録API呼出
      const ret = await postCompareOrderAPI(order);
      return ret?.data;
    },
    /**
     * 照合実績書き込み
     * @param {!object} compare 照合情報
     * @param {!object} item 照合実績画面情報
     * @param {number} detailIndex 明細インデックス
     * @returns {?object} 照合実績情報、null:エラー
     */
    writeCompareResult: async function (compare, item, detailIndex) {
      const detail = item.details[detailIndex];
      const result = {
        compareOrderId: detail.compareOrderId, // 照合予定ID
        compareOrderNo: item.compareOrderNo, // 照合予定NO
        compareResultNo: item.compareResultNo, // 照合実績NO
        readId: item.readId, // 読取ID
        dataType: item.dataType, // データ区分
        headerBarcodeId: item.headerBarcodeId, // ヘッダバーコードID
        detailBarcodeId: item.detailBarcodeId, // 明細バーコードID
        barcodeId: item.barcodeId, // バーコードID
        compareFlg: detail.compareFlg, // 照合フラグ
        compareErr: detail.compareErr // 照合エラー
      };

      // ヘッダ値をセット
      for (let i = 1; i < 10; i++) {
        const headerKey = `headerValueNo${i}`;
        result[headerKey] = item[headerKey];
      }

      // 照合値をセット
      const detailCompares = compare.details.filter(function (x) {
        return x.dataLayout === dataLayout.DETAIL;
      });
      for (let cdetail of detailCompares) {
        const detailKey = `detailValueNo${cdetail.compareNo}`;
        const fromCompareKey = `fromCompareNo${cdetail.compareNo}`;
        const toCompareKey = `toCompareNo${cdetail.compareNo}`;
        result[detailKey] = detail[detailKey];
        result[fromCompareKey] = detail[detailKey];
        result[toCompareKey] = detail[toCompareKey];
      }

      // 照合実績登録API呼出
      const ret = await postCompareResultAPI(result);
      return ret?.data;
    },
    /**
     * 読取実績書き込み
     * @param {!object} compare 照合情報
     * @param {!object} item 照合実績画面情報
     * @param {number} detailIndex 明細インデックス
     * @returns {?object} 照合実績情報、null:エラー
     */
    writeReadResult: async function (compare, item, detailIndex) {
      const detail = item.details[detailIndex];
      const result = {
        compareOrderId: detail.compareOrderId, // 照合予定ID
        compareOrderNo: item.compareOrderNo, // 照合予定NO
        compareResultNo: item.compareResultNo, // 照合実績NO
        readId: item.readId, // 読取ID
        dataType: item.dataType, // データ区分
        headerBarcodeId: item.headerBarcodeId, // ヘッダバーコードID
        detailBarcodeId: item.detailBarcodeId, // 明細バーコードID
        barcodeId: item.barcodeId, // バーコードID
        compareFlg: 0, // 照合フラグ(読取実績時は常に0)
        compareErr: detail.compareErr // 照合エラー
      };

      // ヘッダ値をセット
      result['headerValueNo1'] = item['headerValueNo1'];
      result['headerValueNo2'] = item['headerValueNo2'];

      // 読取値をセット
      const detailCompares = compare.details.filter(function (x) {
        return x.dataLayout === dataLayout.DETAIL;
      });
      for (let cdetail of detailCompares) {
        // 読取実績は、照合先実績にセット
        const detailKey = `detailValueNo${cdetail.compareNo}`;
        const toCompareKey = `toCompareNo${cdetail.compareNo}`;
        result[detailKey] = detail[detailKey];
        result[toCompareKey] = detail[detailKey];
      }

      // 読取実績登録API呼出
      const ret = await postReadResultAPI(result);
      return ret?.data;
    }
  }
};
